import { useEffect, useMemo, useState } from 'react';
import { post } from '../../lib/api.js';
import { useResource } from '../../lib/useResource.js';
import { formatDateTime, todayLocal, vehicleLabel } from '../../lib/format.js';
import { Alert, ErrorBox, Field, Modal, useSubmit } from '../../components/ui.js';

/**
 * Formulário curto da seção 11.2.
 * A base do prazo é sempre explícita na tela e o resultado aparece ANTES de
 * salvar, com o ajuste de calendário e de janela escrito por extenso.
 */

type Unit = 'days' | 'weeks' | 'months' | 'years';

const ATALHOS: { label: string; amount: number; unit: Unit }[] = [
  { label: '1 mês', amount: 1, unit: 'months' },
  { label: '3 meses', amount: 3, unit: 'months' },
  { label: '6 meses', amount: 6, unit: 'months' },
  { label: '1 ano', amount: 1, unit: 'years' },
  { label: '2 anos', amount: 2, unit: 'years' },
];

const UNIDADES: { value: Unit; label: string }[] = [
  { value: 'days', label: 'dias' },
  { value: 'weeks', label: 'semanas' },
  { value: 'months', label: 'meses' },
  { value: 'years', label: 'anos' },
];

interface Preview {
  baseDate: string;
  baseLabel: string;
  requestedAt: string;
  effectiveAt: string;
  effectiveLabel: string;
  adjustments: string[];
  mode: 'single' | 'sequence';
  messageBody: string;
  totalSteps: number;
}

interface Subject {
  id: string;
  name: string;
}
interface Template {
  id: string;
  name: string;
  status: string;
  first_offset_amount: number;
  first_offset_unit: string;
}

export function NovoLembrete({
  customerId,
  vehicles,
  services,
  onClose,
  onCreated,
}: {
  customerId: string;
  vehicles: { id: string; make: string | null; model: string | null; model_year: number | null; plate: string | null }[];
  services?: { id: string; performed_on: string; purpose: string }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const subjects = useResource<{ subjects: Subject[] }>('/api/reminder-subjects');
  const templates = useResource<{ templates: Template[] }>('/api/reminder-templates');

  const [subjectText, setSubjectText] = useState('');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [serviceId, setServiceId] = useState<string>('');
  const [baseKind, setBaseKind] = useState<'today' | 'service_date' | 'custom'>('today');
  const [customBaseDate, setCustomBaseDate] = useState(todayLocal());
  const [whenKind, setWhenKind] = useState<'offset' | 'exact'>('offset');
  const [amount, setAmount] = useState(6);
  const [unit, setUnit] = useState<Unit>('months');
  const [exactDate, setExactDate] = useState(todayLocal());
  const [exactTime, setExactTime] = useState('09:00');
  const [mode, setMode] = useState<'single' | 'sequence'>('single');
  const [templateId, setTemplateId] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [criado, setCriado] = useState<Preview | null>(null);

  const payload = useMemo(() => {
    const [h = '9', m = '0'] = exactTime.split(':');
    return {
      customerId,
      vehicleId: vehicleId || null,
      serviceId: serviceId || null,
      subjectText: subjectText.trim() || 'acompanhamento',
      baseKind,
      customBaseDate: baseKind === 'custom' ? customBaseDate : undefined,
      when:
        whenKind === 'offset'
          ? { kind: 'offset' as const, amount, unit }
          : { kind: 'exact' as const, date: exactDate, minuteOfDay: Number(h) * 60 + Number(m) },
      mode,
      templateId: mode === 'sequence' ? templateId || null : null,
      messageBody: messageBody.trim() ? messageBody : undefined,
    };
  }, [
    customerId, vehicleId, serviceId, subjectText, baseKind, customBaseDate,
    whenKind, amount, unit, exactDate, exactTime, mode, templateId, messageBody,
  ]);

  // Prévia recalculada a cada mudança, com pequena espera para não pesar.
  useEffect(() => {
    if (mode === 'sequence' && !templateId) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      post<Preview>('/api/reminders/standalone/preview', payload)
        .then((result) => {
          setPreview(result);
          setPreviewError(null);
        })
        .catch((err: unknown) => {
          setPreview(null);
          setPreviewError(err);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [payload, mode, templateId]);

  const { submit, saving, error } = useSubmit(async () => {
    const result = await post<{ preview: Preview }>('/api/reminders/standalone', payload);
    setCriado(result.preview);
  });

  const ativos = (templates.data?.templates ?? []).filter((t) => t.status === 'active');

  if (criado) {
    return (
      <Modal
        title="Lembrete criado"
        onClose={() => onCreated()}
        footer={
          <>
            <button type="button" onClick={() => setCriado(null)}>
              Editar lembrete
            </button>
            <button type="button" className="primary" onClick={() => onCreated()}>
              Concluir
            </button>
          </>
        }
      >
        <Alert tone="info" title={criado.effectiveLabel}>
          {criado.mode === 'single'
            ? 'Aviso único. Não haverá repetição.'
            : `Sequência com ${criado.totalSteps} etapas. As etapas seguintes só são agendadas depois que a anterior for aceita pelo provedor.`}
        </Alert>
        <div className="preview-box">{criado.messageBody}</div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Novo lembrete"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-lembrete" className="primary" disabled={saving || !preview}>
            {saving ? 'Salvando…' : 'Criar lembrete'}
          </button>
        </>
      }
    >
      <form id="form-lembrete" onSubmit={submit}>
        <ErrorBox error={error} />

        <div className="field-row">
          <Field label="Assunto ou situação" required hint="Ex.: retorno de troca de óleo, orçamento pendente.">
            <input
              type="text"
              list="assuntos"
              value={subjectText}
              onChange={(e) => setSubjectText(e.target.value)}
              required
              autoFocus
            />
            <datalist id="assuntos">
              {(subjects.data?.subjects ?? []).map((s) => (
                <option key={s.id} value={s.name} />
              ))}
            </datalist>
          </Field>

          <Field label="Veículo" hint="Opcional.">
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Sem veículo</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {vehicleLabel(v)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <fieldset style={{ border: '1px solid var(--gl-border)', borderRadius: 6, padding: 14, marginBottom: 14 }}>
          <legend style={{ fontSize: 13.5, fontWeight: 600, padding: '0 6px' }}>Quando</legend>

          <Field label="Contar a partir de">
            <select
              value={baseKind}
              onChange={(e) => setBaseKind(e.target.value as typeof baseKind)}
            >
              <option value="today">Hoje</option>
              {services?.length ? <option value="service_date">Data de um serviço</option> : null}
              <option value="custom">Outra data</option>
            </select>
          </Field>

          {baseKind === 'service_date' && services?.length ? (
            <Field label="Serviço de referência" required>
              <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} required>
                <option value="">Escolha o serviço</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.performed_on} — {s.purpose}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {baseKind === 'custom' ? (
            <Field label="Data base" required>
              <input
                type="date"
                value={customBaseDate}
                onChange={(e) => setCustomBaseDate(e.target.value)}
                required
              />
            </Field>
          ) : null}

          <div className="row" style={{ marginBottom: 10 }}>
            <label className="checkbox" style={{ marginBottom: 0 }}>
              <input
                type="radio"
                name="quando"
                checked={whenKind === 'offset'}
                onChange={() => setWhenKind('offset')}
              />
              Em um prazo
            </label>
            <label className="checkbox" style={{ marginBottom: 0 }}>
              <input
                type="radio"
                name="quando"
                checked={whenKind === 'exact'}
                onChange={() => setWhenKind('exact')}
              />
              Em uma data
            </label>
          </div>

          {whenKind === 'offset' ? (
            <>
              <div className="row" style={{ marginBottom: 10 }}>
                {ATALHOS.map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    className={amount === a.amount && unit === a.unit ? 'small primary' : 'small'}
                    onClick={() => {
                      setAmount(a.amount);
                      setUnit(a.unit);
                    }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <div className="field-row">
                <Field label="Quantidade">
                  <input
                    type="number"
                    min={1}
                    value={amount}
                    onChange={(e) => setAmount(Math.max(1, Number(e.target.value)))}
                  />
                </Field>
                <Field label="Unidade">
                  <select value={unit} onChange={(e) => setUnit(e.target.value as Unit)}>
                    {UNIDADES.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </>
          ) : (
            <div className="field-row">
              <Field label="Data" required>
                <input type="date" value={exactDate} onChange={(e) => setExactDate(e.target.value)} required />
              </Field>
              <Field label="Horário">
                <input type="time" value={exactTime} onChange={(e) => setExactTime(e.target.value)} />
              </Field>
            </div>
          )}
        </fieldset>

        <div className="field-row">
          <Field label="Modo de envio">
            <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="single">Um único aviso</option>
              <option value="sequence">Uma sequência</option>
            </select>
          </Field>

          {mode === 'sequence' ? (
            <Field
              label="Sequência"
              required
              hint="O modelo fornece as etapas seguintes; a data inicial é a definida acima."
            >
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} required>
                <option value="">Escolha a sequência</option>
                {ativos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>

        <Field
          label="Mensagem"
          hint="Deixe em branco para usar o texto padrão. Variáveis permitidas: {primeiro_nome}, {loja}, {veiculo}, {assunto}."
        >
          <textarea
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            placeholder={preview?.messageBody ?? ''}
          />
        </Field>

        {previewError ? <ErrorBox error={previewError} /> : null}

        {preview ? (
          <Alert tone="info" title={preview.effectiveLabel}>
            <div className="small">
              Prazo contado a partir de {preview.baseLabel} ({preview.baseDate}).
              {preview.mode === 'single'
                ? ' Aviso único: não haverá repetição.'
                : ` Sequência com ${preview.totalSteps} etapas.`}
            </div>
            {preview.adjustments.length ? (
              <ul style={{ margin: '8px 0 0 18px' }}>
                {preview.adjustments.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            ) : null}
            <div className="preview-box" style={{ marginTop: 10 }}>
              {preview.messageBody}
            </div>
            <div className="hint" style={{ marginTop: 6 }}>
              A prévia mostra o texto com as variáveis ainda por preencher. No envio, nome, veículo e
              datas vêm do cadastro.
            </div>
          </Alert>
        ) : null}

        {preview && formatDateTime(preview.requestedAt) !== formatDateTime(preview.effectiveAt) ? (
          <p className="hint">
            Pedido: {formatDateTime(preview.requestedAt)} · Efetivo: {formatDateTime(preview.effectiveAt)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
